<template lang="pug">
FcwSheet.FcwPointOfInterestSheet(
  v-model="isOpen"
  position="left"
  :title="name"
  type="floating"
  @open="emit('open')"
  @close="emit('close')"
)
  template(#subtitle)
    FcwChip.FcwPointOfInterest__tag(
      v-if="tag"
      :label="tag"
    )
  template(#content)
    .FcwPointOfInterestSheet__content
      FcwAddress(
        v-if="address?.lines?.length"
        :address="address?.lines ?? []"
        :coordinates="address?.coordinates"
        :show-route="Boolean(address?.coordinates)"
        :walking-time="walkingTime"
        :distance="distance"
      )
      FcwButtonLink(
        :location="ctaLink"
        static
      )
        span {{ ctaText }}
        FIcon(name="externalLink")
      span.FcwPointOfInterestSheet__lastUpdated(v-if="lastUpdatedAt") {{ t('entities__last_updated_at') }} {{ formatDate(lastUpdatedAt, { format: 'p' }) }}
</template>

<style lang="stylus">
.FcwPointOfInterestSheet__tags
  display flex
  gap rem(8)
  text-transform uppercase

.FcwPointOfInterestSheet__content
  display flex
  flex-direction column
  gap rem(16)
  height inherit

.FcwPointOfInterest__tag
  text-transform uppercase

.FcwPointOfInterestSheet__lastUpdated
  margin-top auto
</style>

<script setup lang="ts">
import { PointOfInterestType } from '@/models/enums';
export interface FcwPointOfInterestSheetProps {
  /**
   * Model value of the sheet
   */
  modelValue: boolean;
  /**
   * Name of the point of interest
   */
  name: string;
  /**
   * Point of interest type
   */
  type: PointOfInterestType;

  /**
   * Address of the point of interest
   */
  address?: {
    lines: string[];
    coordinates: CoordinatesObject;
  };
  /**
   * Walking time to the POI
   */
  walkingTime?: number | null;
  /**
   * Distance to the POI
   */
  distance?: number | null;
  /**
   * Link to the POI website
   */
  ctaLink?: string;
  /**
   * Last time the data has been refreshed
   */
  lastUpdatedAt?: Date | null;
}

const props = withDefaults(defineProps<FcwPointOfInterestSheetProps>(), {
  address: undefined,
  walkingTime: undefined,
  distance: undefined,
  ctaLink: undefined,
  lastUpdatedAt: null,
});
const isOpen = useVModelProxy({ props });

const emit = defineEmits<{
  (name: 'open'): void;
  (name: 'close'): void;
}>();

const { t } = useClientI18n();

const tag = computed(() => {
  switch (props.type) {
    case PointOfInterestType.BikeHouse:
      return t('entities__bike_house');
    case PointOfInterestType.BikeLocker:
      return t('entities__locker');
    default:
      return '';
  }
});

const ctaText = computed(() => {
  switch (props.type) {
    case PointOfInterestType.BikeHouse:
      return t('entities__bike_house__cta');
    case PointOfInterestType.BikeLocker:
      return t('entities__locker__cta');
    default:
      return t('entities__unknown__cta');
  }
});

const { formatDate } = useLocaleFormat();
</script>
